import { IEmployee } from "@gtmhub/employees";
import { IGoal } from "@gtmhub/goals/models";
import { IHubSessionIdWorkflowMap } from "@gtmhub/hub/models/hub";
import { ITeam } from "@gtmhub/teams";
import { IdMap } from "@gtmhub/util";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import dayjs from "@webapp/shared/libs/dayjs";
import { IHomeMetric } from "./home.models";

export function createEmployeeAndTeamMap(employee: IEmployee, teams: ITeam[]): IdMap<IEmployee | ITeam> {
  const map: IdMap<IEmployee | ITeam> = {};
  map[employee.id] = employee;
  teams.forEach((team) => (map[team.id] = team));
  return map;
}

export const isToDoMetric = (metric: Metric) => {
  return !metric.insightName && !metric.sourceMetricId && metric.goalClosedStatus.status === "active" && metric.attainment < 1;
};

// employeeTeamIds,employeeManagedTeamIds
export const getEmployeeTeamIds = (employee: IEmployee): string[] => {
  return [employee.id].concat(employee.managedTeamIds, employee.teamIds).filter((teamId, index, self) => teamId && self.indexOf(teamId) === index);
};

export const getEmployeeTeamIdsAndManagedTeamIds = (employee: IEmployee): string[] => {
  return [...new Set([...employee.managedTeamIds, ...employee.teamIds])];
};

export enum HomeScreenView {
  dashboard = "dashboard",
  feed = "feed",
  next = "Dashboard (next)",
}

const isCloseDeadline = (today: dayjs.Dayjs, target: string) => {
  const dayOfWeek = dayjs(target).utc().day();

  const isSunday = dayOfWeek === 0;
  const isSaturday = dayOfWeek === 6;

  const daysShowing = isSaturday ? 3 : isSunday ? 4 : 2;

  return dayjs(target).diff(today, "days") <= daysShowing;
};

const isBetweenDates = (from: string, to: string, dateToCompare: string | dayjs.Dayjs): boolean => {
  return dateToCompare ? dayjs(dateToCompare).isBetween(from, to, null, "()") : false;
};

export function getGoalsToApprove(goalsToReview: IHubSessionIdWorkflowMap, allSessions: Session[]): IGoal[] {
  const activeFutureSessions = allSessions.filter((s) => s.status !== "closed" && dayjs().isSameOrBefore(s.end));

  return Object.keys(goalsToReview).reduce((acc, sessionId) => {
    const isActiveFutureSession = activeFutureSessions.some((s) => s.id === sessionId);

    const notApprovedGoals = goalsToReview[sessionId].goalsToReview
      .filter((goal) => !goal.currentUserApproved && isActiveFutureSession)
      .map((goal) => ({ ...goal, deadline: activeFutureSessions.find((s) => s.id === sessionId).start, sessionId }));

    return acc.concat(notApprovedGoals);
  }, []);
}

export function getNotUpdatedMetricsInCurrentCadence(metrics: IHomeMetric[], sessionsMap: Record<string, Session>, employee: IEmployee): IHomeMetric[] {
  const employeeIdAndManagedTeamIds = employee.managedTeamIds.concat(employee.id);
  // todo-items start to show at least on Wednesday, so if you need to debug/test something, increment the date or hard-code it to at least Wednesday
  // in-order to see your items
  const today = dayjs();

  // user can be a part of many teams, currently we show only todo-metrics where user is owner, or the metric belongs to a team he is a manager of
  return (
    metrics
      .filter((metric) => {
        if (sessionsMap[metric.sessionId] && sessionsMap[metric.sessionId].cadence) {
          const currentCadance = sessionsMap[metric.sessionId].cadence.ranges.find((range) => isBetweenDates(range.start, range.end, today));

          if (currentCadance) {
            const isNotUpdatedInCadance = !isBetweenDates(currentCadance.start, currentCadance.end, metric.lastCheckInDate);
            const isNearDeadline = isCloseDeadline(today, currentCadance.end);

            if (isNotUpdatedInCadance && isNearDeadline) {
              metric.cadanceDeadline = currentCadance.end;
            }
            return isNotUpdatedInCadance && isNearDeadline;
          }
        }

        return false;
      })
      // due to optimisation, we get all metrics for the userId,userTeamId,userManagedTeamId to reduce http calls
      // in todo-list we show only userId,userManagedTeamId so thats why we filter them here
      .filter((metric) => metric.ownerIds.some((ownerId) => employeeIdAndManagedTeamIds.includes(ownerId)))
      // metrics with no deadline dueDate have default one "0001-01-01T00:00:00Z"
      // metrics with hard deadline which is due by today aren't shown in todo-section
      .filter((metric) => metric.dueDate === "0001-01-01T00:00:00Z" || today.isSameOrBefore(metric.dueDate))
      // updated boolean metrics aren't shown in todo-list
      .filter((metric) => metric.manualType !== "boolean" || metric.attainment === 0)
  );
}

export function getActiveSessionsMap(sessions: Session[]): Record<string, Session> {
  return sessions.reduce((agg, session) => {
    agg[session.id] = session;
    return agg;
  }, {} as Record<string, Session>);
}
